import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker - Use local worker file in build assets
pdfjsLib.GlobalWorkerOptions.workerSrc = '/build/assets/pdf.worker.min.mjs';

/**
 * Extract individual pages from a PDF file as image blobs
 * @param {File} pdfFile - The PDF file to process
 * @param {Object} options - Processing options
 * @param {number} options.scale - Scale factor for rendering (default: 2.0 for high quality)
 * @param {string} options.format - Image format: 'jpeg' or 'png' (default: 'jpeg')
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.85)
 * @param {Function} options.onProgress - Callback(currentPage, totalPages)
 * @param {boolean} options.combinePages - Combine all pages into one tall image (default: false)
 * @returns {Promise<Array>} Array of {pageNumber, blob, file} objects
 */
export async function extractPdfPages(pdfFile, options = {}) {
    const {
        scale = 2.0,
        format = 'jpeg',
        quality = 0.85,
        onProgress = null,
        combinePages = false
    } = options;

    try {
        // Read PDF file as ArrayBuffer
        const arrayBuffer = await readFileAsArrayBuffer(pdfFile);

        // Load PDF document
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        const totalPages = pdf.numPages;

        console.log(`PDF loaded: ${totalPages} pages`);

        // If combinePages is true, combine all pages into one tall image
        if (combinePages && totalPages > 1) {
            return await combinePdfPagesIntoOne(pdf, pdfFile, scale, format, quality, onProgress);
        }

        const pageImages = [];

        // Process each page
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            if (onProgress) {
                onProgress(pageNum, totalPages);
            }

            const page = await pdf.getPage(pageNum);
            const viewport = page.getViewport({ scale });

            // Create canvas for rendering
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            canvas.height = viewport.height;
            canvas.width = viewport.width;

            // Render PDF page to canvas
            await page.render({
                canvasContext: context,
                viewport: viewport
            }).promise;

            // Convert canvas to blob
            const blob = await canvasToBlob(canvas, format, quality);

            // Create File object with proper naming
            const originalName = pdfFile.name.replace(/\.pdf$/i, '');
            const extension = format === 'jpeg' ? 'jpg' : 'png';
            const fileName = totalPages > 1
                ? `${originalName}_page_${pageNum}.${extension}`
                : `${originalName}.${extension}`;

            const file = new File([blob], fileName, {
                type: `image/${format === 'jpeg' ? 'jpeg' : 'png'}`,
                lastModified: Date.now()
            });

            pageImages.push({
                pageNumber: pageNum,
                blob,
                file,
                width: canvas.width,
                height: canvas.height
            });

            console.log(`Extracted page ${pageNum}/${totalPages}: ${fileName} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);
        }

        return pageImages;

    } catch (error) {
        console.error('Error extracting PDF pages:', error);
        throw new Error(`Failed to process PDF: ${error.message}`);
    }
}

/**
 * Combine all PDF pages into one tall image
 * @param {PDFDocumentProxy} pdf - Loaded PDF document
 * @param {File} pdfFile - Original PDF file
 * @param {number} scale - Scale factor
 * @param {string} format - Image format
 * @param {number} quality - Image quality
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Array>} Array with single combined image
 */
async function combinePdfPagesIntoOne(pdf, pdfFile, scale, format, quality, onProgress) {
    const totalPages = pdf.numPages;
    const canvases = [];
    
    // First, render all pages to individual canvases
    for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (onProgress) {
            onProgress(pageNum, totalPages);
        }

        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale });

        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        await page.render({
            canvasContext: context,
            viewport: viewport
        }).promise;

        canvases.push(canvas);
        console.log(`Rendered page ${pageNum}/${totalPages} for combining`);
    }

    // Calculate combined dimensions
    let totalHeight = 0;
    let maxWidth = 0;
    
    canvases.forEach(canvas => {
        totalHeight += canvas.height;
        maxWidth = Math.max(maxWidth, canvas.width);
    });

    // Create combined canvas
    const combinedCanvas = document.createElement('canvas');
    combinedCanvas.width = maxWidth;
    combinedCanvas.height = totalHeight;
    const ctx = combinedCanvas.getContext('2d');

    // Fill with white background
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, maxWidth, totalHeight);

    // Draw all pages vertically
    let currentY = 0;
    canvases.forEach((canvas, index) => {
        // Center horizontally if page is narrower than max width
        const x = (maxWidth - canvas.width) / 2;
        ctx.drawImage(canvas, x, currentY);
        currentY += canvas.height;
        console.log(`Combined page ${index + 1}/${totalPages}`);
    });

    // Convert combined canvas to blob
    const blob = await canvasToBlob(combinedCanvas, format, quality);

    // Create File object
    const originalName = pdfFile.name.replace(/\.pdf$/i, '');
    const extension = format === 'jpeg' ? 'jpg' : 'png';
    const fileName = `${originalName}_combined.${extension}`;

    const file = new File([blob], fileName, {
        type: `image/${format === 'jpeg' ? 'jpeg' : 'png'}`,
        lastModified: Date.now()
    });

    console.log(`✓ Combined ${totalPages} PDF pages into single image: ${fileName} (${(blob.size / 1024 / 1024).toFixed(2)} MB)`);

    return [{
        pageNumber: 1,
        blob,
        file,
        width: combinedCanvas.width,
        height: combinedCanvas.height
    }];
}

/**
 * Check if a file is a PDF
 * @param {File} file
 * @returns {boolean}
 */
export function isPDF(file) {
    return file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
}

/**
 * Get page count of a PDF without extracting images
 * @param {File} pdfFile
 * @returns {Promise<number>}
 */
export async function getPdfPageCount(pdfFile) {
    try {
        const arrayBuffer = await readFileAsArrayBuffer(pdfFile);
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        return pdf.numPages;
    } catch (error) {
        console.error('Error reading PDF page count:', error);
        return 1; // Assume single page on error
    }
}

/**
 * Helper: Read file as ArrayBuffer
 */
function readFileAsArrayBuffer(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = (e) => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
    });
}

/**
 * Helper: Convert canvas to Blob
 */
function canvasToBlob(canvas, format, quality) {
    return new Promise((resolve, reject) => {
        const mimeType = format === 'jpeg' ? 'image/jpeg' : 'image/png';

        if (canvas.toBlob) {
            canvas.toBlob(
                (blob) => {
                    if (blob) {
                        resolve(blob);
                    } else {
                        reject(new Error('Failed to convert canvas to blob'));
                    }
                },
                mimeType,
                quality
            );
        } else {
            // Fallback for older browsers
            const dataUrl = canvas.toDataURL(mimeType, quality);
            const blob = dataURLtoBlob(dataUrl);
            resolve(blob);
        }
    });
}

/**
 * Helper: Convert data URL to Blob (fallback)
 */
function dataURLtoBlob(dataUrl) {
    const parts = dataUrl.split(',');
    const mimeMatch = parts[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/png';
    const bstr = atob(parts[1]);
    const n = bstr.length;
    const u8arr = new Uint8Array(n);

    for (let i = 0; i < n; i++) {
        u8arr[i] = bstr.charCodeAt(i);
    }

    return new Blob([u8arr], { type: mime });
}
